import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { sendTestEmail } from '@/lib/api';

type EmailTarget = 'admin' | 'user';

const TEMPLATES: Array<{ id: string; label: string; target: EmailTarget; subject: string; text: string; html: string }> = [
  {
    id: 'welcome-admin',
    label: 'Admin Welcome',
    target: 'admin',
    subject: 'TGMCI Admin Dashboard — Email Delivery Test',
    text: 'This is a test email sent from the TGMCI admin dashboard to confirm that email delivery is working correctly. If you received this, Resend is configured properly.',
    html: `<div style="font-family:sans-serif;max-width:600px;margin:0 auto;padding:32px 24px">
  <div style="text-align:center;margin-bottom:32px">
    <div style="display:inline-block;background:#d97706;color:#fff;font-weight:bold;font-size:18px;padding:10px 20px;border-radius:8px">TGMCI</div>
  </div>
  <h1 style="font-size:22px;font-weight:700;color:#111827;margin:0 0 12px">Admin Email Test</h1>
  <p style="color:#374151;font-size:15px;line-height:1.6;margin:0 0 16px">This is a test email sent from the <strong>TGMCI Admin Dashboard</strong> to confirm that email delivery is working correctly.</p>
  <p style="color:#374151;font-size:15px;line-height:1.6;margin:0 0 24px">If you received this, your Resend configuration is working. Real notifications (contact forms, prayer requests, etc.) will appear in your inbox.</p>
  <div style="background:#f9fafb;border-left:4px solid #d97706;padding:16px;border-radius:0 8px 8px 0;margin-bottom:24px">
    <p style="margin:0;color:#6b7280;font-size:13px">Sent from TGMCI Admin · The Great Multitude Church International</p>
  </div>
  <p style="color:#9ca3af;font-size:12px;margin:0">You can close this test email. This message was triggered manually by a site administrator.</p>
</div>`,
  },
  {
    id: 'newsletter-confirm',
    label: 'Newsletter Confirmation (User)',
    target: 'user',
    subject: 'Welcome to the TGMCI Community Newsletter!',
    text: 'Thank you for subscribing to the TGMCI newsletter. You will receive updates about our church events, sermons, and community news. God bless you!',
    html: `<div style="font-family:sans-serif;max-width:600px;margin:0 auto;padding:32px 24px">
  <div style="text-align:center;margin-bottom:32px">
    <div style="display:inline-block;background:#d97706;color:#fff;font-weight:bold;font-size:18px;padding:10px 20px;border-radius:8px">TGMCI</div>
  </div>
  <h1 style="font-size:22px;font-weight:700;color:#111827;margin:0 0 12px">Welcome to the TGMCI Community!</h1>
  <p style="color:#374151;font-size:15px;line-height:1.6;margin:0 0 16px">Thank you for subscribing to our newsletter. You are now part of our growing faith community!</p>
  <p style="color:#374151;font-size:15px;line-height:1.6;margin:0 0 24px">You will receive updates about our church events, sermons, testimonies, and community news directly in your inbox.</p>
  <div style="text-align:center;margin-bottom:28px">
    <a href="https://tgmci.org" style="background:#d97706;color:#fff;text-decoration:none;padding:14px 28px;border-radius:8px;font-weight:600;font-size:15px;display:inline-block">Visit Our Website</a>
  </div>
  <hr style="border:none;border-top:1px solid #e5e7eb;margin:24px 0">
  <p style="color:#374151;font-size:14px;line-height:1.6;text-align:center;font-style:italic">"For where two or three gather in my name, there am I with them." — Matthew 18:20</p>
  <p style="color:#9ca3af;font-size:12px;text-align:center;margin-top:16px">The Great Multitude Church International · Edea, Cameroon &amp; Accra, Ghana</p>
</div>`,
  },
  {
    id: 'prayer-confirm',
    label: 'Prayer Request Received (User)',
    target: 'user',
    subject: 'Your Prayer Request Has Been Received — TGMCI',
    text: 'We have received your prayer request and our prayer team is interceding for you. You are not alone — we are standing with you in faith. God bless you.',
    html: `<div style="font-family:sans-serif;max-width:600px;margin:0 auto;padding:32px 24px">
  <div style="text-align:center;margin-bottom:32px">
    <div style="display:inline-block;background:#d97706;color:#fff;font-weight:bold;font-size:18px;padding:10px 20px;border-radius:8px">TGMCI</div>
  </div>
  <h1 style="font-size:22px;font-weight:700;color:#111827;margin:0 0 12px">We Are Praying for You</h1>
  <p style="color:#374151;font-size:15px;line-height:1.6;margin:0 0 16px">We have received your prayer request and our dedicated prayer team is interceding on your behalf.</p>
  <p style="color:#374151;font-size:15px;line-height:1.6;margin:0 0 24px">You are not alone in this — we are standing with you in faith. Trust that God hears every prayer and that His perfect plan is unfolding in your life.</p>
  <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:20px;margin-bottom:24px;text-align:center">
    <p style="color:#92400e;font-size:14px;font-style:italic;margin:0">"Do not be anxious about anything, but in every situation, by prayer and petition, with thanksgiving, present your requests to God." — Philippians 4:6</p>
  </div>
  <p style="color:#374151;font-size:14px;line-height:1.6;margin:0 0 8px">If you would like to speak directly with one of our pastors, please contact us at <a href="mailto:info@tgmci.org" style="color:#d97706">info@tgmci.org</a>.</p>
  <p style="color:#9ca3af;font-size:12px;margin-top:24px;text-align:center">The Great Multitude Church International · Edea, Cameroon &amp; Accra, Ghana</p>
</div>`,
  },
  {
    id: 'contact-ack',
    label: 'Contact Acknowledgement (User)',
    target: 'user',
    subject: 'We Received Your Message — TGMCI',
    text: 'Thank you for reaching out to us at TGMCI. We have received your message and will get back to you within 1-2 business days. God bless you!',
    html: `<div style="font-family:sans-serif;max-width:600px;margin:0 auto;padding:32px 24px">
  <div style="text-align:center;margin-bottom:32px">
    <div style="display:inline-block;background:#d97706;color:#fff;font-weight:bold;font-size:18px;padding:10px 20px;border-radius:8px">TGMCI</div>
  </div>
  <h1 style="font-size:22px;font-weight:700;color:#111827;margin:0 0 12px">Thank You for Reaching Out!</h1>
  <p style="color:#374151;font-size:15px;line-height:1.6;margin:0 0 16px">We have received your message and one of our team members will be in touch with you within <strong>1-2 business days</strong>.</p>
  <p style="color:#374151;font-size:15px;line-height:1.6;margin:0 0 24px">We value every conversation and look forward to connecting with you. If your matter is urgent, please call us directly.</p>
  <div style="background:#f9fafb;border-radius:8px;padding:20px;margin-bottom:24px">
    <p style="margin:0 0 8px;font-weight:600;color:#111827;font-size:14px">Contact Us Directly</p>
    <p style="margin:0 0 4px;color:#374151;font-size:13px">Email: <a href="mailto:info@tgmci.org" style="color:#d97706">info@tgmci.org</a></p>
    <p style="margin:0;color:#374151;font-size:13px">Website: <a href="https://tgmci.org" style="color:#d97706">tgmci.org</a></p>
  </div>
  <p style="color:#9ca3af;font-size:12px;margin:0;text-align:center">The Great Multitude Church International · Edea, Cameroon &amp; Accra, Ghana</p>
</div>`,
  },
];

export default function TestEmailPage() {
  const { user } = useAuth();

  const [recipientType, setRecipientType] = useState<'self' | 'custom'>('self');
  const [customEmail, setCustomEmail] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>(TEMPLATES[0].id);
  const [customSubject, setCustomSubject] = useState('');
  const [customBody, setCustomBody] = useState('');
  const [useCustom, setUseCustom] = useState(false);

  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const template = TEMPLATES.find((t) => t.id === selectedTemplate) ?? TEMPLATES[0];

  const getRecipient = () => {
    if (recipientType === 'self') return user?.email ?? '';
    return customEmail.trim();
  };

  const handleSend = async () => {
    const to = getRecipient();
    if (!to) {
      setResult({ success: false, message: 'Please enter a recipient email address.' });
      return;
    }

    setSending(true);
    setResult(null);

    try {
      const subject = useCustom ? customSubject : template.subject;
      const text = useCustom ? customBody : template.text;
      const html = useCustom ? undefined : template.html;

      if (!subject.trim() || !text.trim()) {
        setResult({ success: false, message: 'Subject and body are required.' });
        return;
      }

      await sendTestEmail({ to, subject, text, html });
      setResult({
        success: true,
        message: `Test email sent successfully to ${to}. Check the inbox — it may take a moment to arrive.`,
      });
    } catch (err) {
      setResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to send email.',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-heading font-semibold text-xl text-foreground-900">Email Delivery Test</h2>
        <p className="text-sm text-foreground-500 mt-1">
          Send a test email to confirm Resend is properly configured and email delivery is working.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Settings */}
        <div className="space-y-6">
          {/* Recipient */}
          <div className="bg-white rounded-lg border border-background-200 p-6">
            <h3 className="font-heading font-semibold text-base text-foreground-900 mb-4">Recipient</h3>

            <div className="flex gap-1 bg-background-100 rounded-full p-1 mb-4 w-fit">
              <button
                type="button"
                onClick={() => setRecipientType('self')}
                className={`px-4 py-2 rounded-full text-sm font-label font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  recipientType === 'self' ? 'bg-primary-500 text-white' : 'text-foreground-600'
                }`}
              >
                Send to myself
              </button>
              <button
                type="button"
                onClick={() => setRecipientType('custom')}
                className={`px-4 py-2 rounded-full text-sm font-label font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  recipientType === 'custom' ? 'bg-primary-500 text-white' : 'text-foreground-600'
                }`}
              >
                Custom email
              </button>
            </div>

            {recipientType === 'self' ? (
              <div className="px-4 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-700">
                <i className="ri-mail-line mr-2 text-foreground-400" aria-hidden="true" />
                {user?.email}
              </div>
            ) : (
              <input
                type="email"
                value={customEmail}
                onChange={(e) => setCustomEmail(e.target.value)}
                placeholder="recipient@example.com"
                className="w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 outline-none focus:ring-2 focus:ring-primary-300"
              />
            )}
          </div>

          {/* Template */}
          <div className="bg-white rounded-lg border border-background-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-base text-foreground-900">Email Template</h3>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-foreground-600">
                <input
                  type="checkbox"
                  checked={useCustom}
                  onChange={(e) => setUseCustom(e.target.checked)}
                  className="w-4 h-4 accent-primary-500"
                />
                Write custom
              </label>
            </div>

            {!useCustom ? (
              <div className="space-y-2">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSelectedTemplate(t.id)}
                    className={`w-full text-left px-4 py-3 rounded-lg border transition-colors cursor-pointer ${
                      selectedTemplate === t.id
                        ? 'border-primary-300 bg-primary-50'
                        : 'border-background-200 hover:border-background-300 hover:bg-background-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground-800">{t.label}</span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-label font-medium ${
                          t.target === 'admin'
                            ? 'bg-secondary-100 text-secondary-900'
                            : 'bg-accent-100 text-accent-900'
                        }`}
                      >
                        {t.target === 'admin' ? 'Admin' : 'User'}
                      </span>
                    </div>
                    <div className="text-xs text-foreground-400 mt-1 truncate">{t.subject}</div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">Subject</label>
                  <input
                    type="text"
                    value={customSubject}
                    onChange={(e) => setCustomSubject(e.target.value)}
                    placeholder="Your email subject"
                    className="w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">Message Body</label>
                  <textarea
                    value={customBody}
                    onChange={(e) => setCustomBody(e.target.value)}
                    placeholder="Write your custom email message here..."
                    rows={6}
                    className="w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 outline-none focus:ring-2 focus:ring-primary-300 resize-y"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Send Button */}
          <button
            type="button"
            onClick={handleSend}
            disabled={sending}
            className="w-full btn-primary py-3 text-sm font-semibold"
          >
            {sending ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-2" aria-hidden="true" />
                Sending...
              </>
            ) : (
              <>
                <i className="ri-send-plane-line mr-2" aria-hidden="true" />
                Send Test Email
              </>
            )}
          </button>

          {result && (
            <div
              className={`px-4 py-3 rounded-md text-sm border ${
                result.success
                  ? 'bg-green-50 border-green-200 text-green-700'
                  : 'bg-red-50 border-red-200 text-red-600'
              }`}
            >
              <div className="flex items-start gap-2">
                <i
                  className={`${result.success ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} text-base mt-0.5`}
                  aria-hidden="true"
                />
                <span>{result.message}</span>
              </div>
            </div>
          )}
        </div>

        {/* Right: Preview */}
        <div className="bg-white rounded-lg border border-background-200 p-6">
          <h3 className="font-heading font-semibold text-base text-foreground-900 mb-4">
            {useCustom ? 'Custom Email' : 'Email Preview'}
          </h3>

          {useCustom ? (
            <div className="space-y-3">
              <div className="rounded-md bg-background-100 px-4 py-3 text-sm text-foreground-700">
                <span className="text-xs font-label uppercase tracking-wider text-foreground-400 block mb-1">Subject</span>
                {customSubject || <span className="text-foreground-300">No subject yet</span>}
              </div>
              <div className="rounded-md bg-background-100 px-4 py-3 text-sm text-foreground-700 whitespace-pre-wrap min-h-[160px]">
                <span className="text-xs font-label uppercase tracking-wider text-foreground-400 block mb-1">Body</span>
                {customBody || <span className="text-foreground-300">Your email message will appear here</span>}
              </div>
            </div>
          ) : (
            <div>
              <div className="rounded-md bg-background-100 px-4 py-2.5 text-sm text-foreground-700 mb-4">
                <span className="text-xs font-label uppercase tracking-wider text-foreground-400 mr-2">Subject:</span>
                {template.subject}
              </div>
              <div
                className="rounded-lg border border-background-200 overflow-auto"
                style={{ minHeight: 320 }}
                dangerouslySetInnerHTML={{ __html: template.html }}
              />
            </div>
          )}

          <div className="mt-6 border-t border-background-200 pt-5">
            <h4 className="text-xs font-label font-semibold text-foreground-600 uppercase tracking-wider mb-3">
              Configuration Status
            </h4>
            <div className="space-y-2">
              <StatusRow label="Resend API Key" value="Configured" ok />
              <StatusRow label="Sending Domain" value="Check Supabase Secrets for RESEND_FROM_DOMAIN" ok={!!import.meta.env.VITE_PUBLIC_SUPABASE_URL} />
              <StatusRow label="Auth Status" value={user?.email ? `Signed in as ${user.email}` : 'Not signed in'} ok={!!user?.email} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusRow({ label, value, ok }: { label: string; value: string; ok: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="text-foreground-600 flex-shrink-0">{label}</span>
      <span className={`font-medium text-right ${ok ? 'text-green-600' : 'text-red-500'}`}>
        <i className={`${ok ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} mr-1`} aria-hidden="true" />
        {value}
      </span>
    </div>
  );
}